import React from 'react';
import { motion } from 'framer-motion';
import { 
  Zap, 
  Activity, 
  Globe, 
  Server, 
  Clock, 
  Shield,
  Database,
  Network,
  ArrowRight,
  MessageSquare,
  Truck,
  Users
} from 'lucide-react';
import { Link } from 'react-router-dom';

// Reusable components
const MetricCard = ({ icon: Icon, value, label }) => (
    <motion.div
      className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10 flex flex-col items-center text-center"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      whileHover={{ borderColor: "rgba(99,102,241,0.5)" }}
    >
      <div className="flex items-center justify-center mb-4">
        <div className="p-3 rounded-lg bg-indigo-500/10">
          <Icon className="w-6 h-6 text-indigo-400" />
        </div>
      </div>
      <div className="text-3xl font-bold text-white mb-2">{value}</div>
      <p className="text-gray-400">{label}</p>
    </motion.div>
);

const FeatureDetail = ({ icon: Icon, title, description }) => (
  <motion.div
    className="flex gap-6"
    initial={{ opacity: 0, x: -20 }}
    whileInView={{ opacity: 1, x: 0 }}
    viewport={{ once: true }}
  >
    <div className="flex-shrink-0">
      <div className="w-12 h-12 rounded-lg bg-indigo-500/10 flex items-center justify-center">
        <Icon className="w-6 h-6 text-indigo-400" />
      </div>
    </div>
    <div>
      <h3 className="text-xl font-semibold text-white mb-2">{title}</h3>
      <p className="text-gray-300 leading-relaxed">{description}</p>
    </div>
  </motion.div>
);

const RealTimeOperations = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-indigo-900 to-purple-900">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,_rgba(99,102,241,0.15),transparent_70%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_60%,_rgba(192,132,252,0.15),transparent_70%)]" />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            className="max-w-4xl mx-auto text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="inline-block mb-6">
              <div className="flex items-center gap-2 text-sm text-indigo-400 bg-indigo-500/10 rounded-full px-4 py-1.5 border border-indigo-500/20">
                <Zap className="w-4 h-4" />
                Real-Time Operations
              </div>
            </div>
            
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
              Lightning-Fast Operations at Scale
            </h1>
            
            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
              Experience unparalleled real-time visibility and control across your entire logistics network,
              powered by enterprise-grade WebSocket architecture.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/register"
                className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-8 py-3 rounded-lg font-medium hover:opacity-90 transition-opacity"
              >
                Get Started
              </Link>
              <Link
                to="/contact"
                className="text-white bg-white/10 px-8 py-3 rounded-lg font-medium hover:bg-white/20 transition-colors"
              >
                Contact Sales
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Metrics Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <MetricCard
              icon={Clock}
              value="<50ms"
              label="Average Response Time"
            />
            <MetricCard
              icon={Activity}
              value="99.99%"
              label="System Uptime"
            />
            <MetricCard
              icon={Globe}
              value="1M+"
              label="Daily Real-Time Events"
            />
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 bg-gray-900/50">
        <div className="container mx-auto px-4">
          <motion.div
            className="text-center max-w-3xl mx-auto mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Enterprise-Grade Real-Time Features
            </h2>
            <p className="text-gray-300">
              Built for scale with reliability and performance at its core
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 max-w-5xl mx-auto">
            <FeatureDetail
              icon={Network}
              title="WebSocket Architecture"
              description="Bi-directional communication channels enabling instant updates and real-time data synchronization across your entire network."
            />
            <FeatureDetail
              icon={Database}
              title="Distributed Data Storage"
              description="Multi-region data replication ensuring sub-millisecond access times and guaranteed data consistency."
            />
            <FeatureDetail
              icon={Shield}
              title="Enterprise Security"
              description="End-to-end encryption, secure WebSocket connections, and comprehensive audit logging for all real-time operations."
            />
            <FeatureDetail
              icon={Server}
              title="Auto-Scaling Infrastructure"
              description="Cloud-native architecture that automatically scales to handle millions of concurrent real-time connections."
            />
            <FeatureDetail
              icon={MessageSquare}
              title="Real-Time Notifications"
              description="Instant alerts and updates delivered across multiple channels including web, mobile, and email."
            />
            <FeatureDetail
              icon={Users}
              title="Collaborative Operations"
              description="Real-time collaboration features enabling team members to work together seamlessly across locations."
            />
          </div>
        </div>
      </section>

      {/* Live Demo Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <motion.div
            className="max-w-4xl mx-auto bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 overflow-hidden"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="p-6 border-b border-white/10">
              <h3 className="text-xl font-semibold text-white">Live System Demo</h3>
              <p className="text-gray-400 mt-1">Watch our real-time system in action</p>
            </div>
            <div className="p-6">
              {/* Add your live demo component here */}
              <div className="aspect-video bg-gray-900/50 rounded-lg flex items-center justify-center">
                <p className="text-gray-400">Live Demo Placeholder</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <motion.div
            className="max-w-4xl mx-auto text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Ready to Transform Your Operations?
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              Join industry leaders already leveraging our real-time platform
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/register"
                className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-8 py-3 rounded-lg font-medium hover:opacity-90 transition-opacity flex items-center"
              >
                Get Started Now
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
              <Link
                to="/contact"
                className="text-white bg-white/10 px-8 py-3 rounded-lg font-medium hover:bg-white/20 transition-colors"
              >
                Schedule Demo
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default RealTimeOperations;